import { state, debugLog } from "./state.js";
import { generateEventId, getExternalId, getClickIds, getArrivalClickIds, getFbp, getFbc } from "./cookies.js";
import { getSourceParams } from "./source-params.js";
import { resolveConsent, consentSource } from "./consent.js";
import type { EventData, EventPayload } from "./types.js";
import { googleSessionForEvent } from "./google-session-browser.js";

/** Build the event payload from base state + caller-provided data. */
export function buildPayload(data: EventData | undefined): EventPayload {
  debugLog("buildPayload", "data.pageUrl=" + (data && data.pageUrl), "href=" + window.location.href);
  const clickIds = getClickIds();
  // What was on the URL when this page loaded, which is a different question
  // from what is in the cookie jar. See getArrivalClickIds.
  const arrivalClickIds = getArrivalClickIds();
  const payload: EventPayload = {
    websiteId: state.websiteId,
    pageUrl: (data && data.pageUrl) || window.location.href,
    eventId: generateEventId(),
    userAgent: navigator.userAgent,
    fbp: getFbp(),
    fbc: getFbc(),
    externalId: getExternalId(),
    // Present only when consent allowed it to be measured at all.
    fp: state.fingerprint ?? undefined,
    timestamp: Math.floor(Date.now() / 1000),
    referrerUrl: document.referrer || undefined,
    browserPixelFired: false,
    clickIds: Object.keys(clickIds).length > 0 ? clickIds : undefined,
    // Always sent, empty object included: the server tells "this script knows
    // the difference and there was nothing on the URL" apart from "this is an
    // older bundle that cannot answer" by whether the field is there at all.
    arrivalClickIds,
    sourceParams: getSourceParams() ?? undefined,
    pixelsFired: [],
  };

  if (data) {
    if (data.eventName) payload.eventName = data.eventName;
    // Deterministic caller ids (ord_<order_id>) make the DB unique index and
    // platform-side dedup catch cross-source duplicates
    if (data.eventId) payload.eventId = data.eventId;
    if (data.eventValue != null) payload.eventValue = data.eventValue;
    if (data.currency) payload.currency = data.currency;
    if (data.browserPixelFired) payload.browserPixelFired = data.browserPixelFired;
    if (data.customData) payload.customData = data.customData;
    if (data.trackingRuleId) payload.trackingRuleId = data.trackingRuleId;
    if (data.source) payload.source = data.source;
  }

  if (Object.keys(state.userData).length > 0) {
    payload.userData = state.userData;
  }

  // Consent state at send time: downstream adapters that must forward
  // consent signals (Google Data Manager, GA4) read this instead of assuming
  // GRANTED (CAPI continues at marketing denied, so the flag matters)
  payload.consent = resolveConsent();
  if (state.config?.platforms?.google_ads || state.config?.platforms?.shopify?.appCollector) {
    const googleSession = googleSessionForEvent(payload.consent.marketing === "granted");
    if (googleSession) payload.userData = { ...payload.userData, googleSession };
  }
  // En waar die vandaan komt. Zonder dit kan het dashboard niet zien of de
  // banner van de klant ooit is aangesloten, en blijft die fout stil.
  payload.consentSource = consentSource();
  if (navigator.language) payload.locale = navigator.language;
  // Reddit takes these as a match key. `screen`, not `innerWidth`: the viewport
  // moves when the reader rotates the phone or opens the console, and a value
  // that changes within one session is worse than a missing one. Guarded
  // because a headless or embedded runtime can leave window.screen undefined,
  // and a payload that throws here loses the whole event.
  try {
    const size = window.screen;
    if (size && size.width > 0 && size.height > 0) {
      payload.screen = { w: size.width, h: size.height };
    }
  } catch {
    // No screen to report; the event is worth more than the signal.
  }

  return payload;
}
